
import React, { Fragment, useEffect, useState } from 'react';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { FormattedMessage } from 'react-intl';


// Components

import ApplicantDocumentInfo from '@components/documentsVerification/ApplicantDocumentInfo';
import BackToDashboard from '@common-components/backToDashboard';


// ActionCreators

import {
    fetchVerificationDocuments,
    overrideDocuments,
    removeDocument,
    submitDocuments,
    uploadDocument,
    viewUploadedDocument
} from '@actions/documentsUpload/documentsVerificationActionCreators';

import { updateEnvironmentProp } from '@actions/common/actionCreators';

import { showModal } from '@actions/common/actionCreators';

// Hooks

import { useDocumentVerificationMockDataUpdate } from '@hooks/useDocumentVerificationMockDataUpdate';

// Utils

import { urlTools } from '@utils/tools/urlTools';
import { checkIfConfigFails } from '@utils/miscellaneous/getVerificationDocumentsConfig';

// Contexts

import { FullDataContext } from '@contexts/documentsVerificationContexts';
import { ContextProvider } from '@contexts/documentsVerificationContexts';

// Constants

import { commonModalTypes } from '@constants/common/modals/commonModalTypes';
import { localStorageHyperDashboardTools } from '@utils/tools/localStorageHyperDashboardTools';
import states from '@constants/configs/states';


const VerificationDocumentsContainer = props => {

    const {
        actions: {
            fetchVerificationDocuments,
            overrideDocuments,
            removeDocument,
            showModal,
            submitDocuments,
            updateEnvironmentProp,
            uploadDocument,
            viewUploadedDocument
        },
        environment: {
            stateCode,
            user: {
                role: userRole
            }
        }
    } = props;

    interface ApplicantType {
        applicantGuid: string,
        event: string
    }

    interface LocalStateTypes {
        applicants: ApplicantType[],
        caseNumber: string
    }

    const INITIAL_STATE = {
        applicants: [],
        caseNumber: ''
    };

    const [ data, updateData ] = useState<LocalStateTypes>(INITIAL_STATE);

    const [ updateMockData ] = useDocumentVerificationMockDataUpdate(data, updateData);

    const {
        applicants,
        caseNumber
    } = data;

    const isUploadHyperDashboardShown =
        localStorageHyperDashboardTools.getValueByPropName(
            'isUploadDocumentsDashboardShown',
            false as any,
        );

    const refreshDocuments = async () => {
        const caseNumber: number = urlTools.getParameter<Number>('caseNumber');

        fetchVerificationDocuments(caseNumber, stateCode).then(response => {
            if (response.success) {
                updateData(response.payload);
            } else {
                showModal({
                    modalType: commonModalTypes.REQUEST_ERROR,
                    modalData: {
                        message: response.message,
                    },
                });
            }
        });
    };

    useEffect(() => {
        refreshDocuments();
    }, [ ]);

    const getContent = () => {

        return applicants.map(applicantInfo => {

            const isConfigNotFound = checkIfConfigFails(stateCode, applicantInfo.event, true);
            const eventNotSupported = !!(
                [
                    states.PENNSYLVANIA,
                    states.NEVADA,
                    states.NEW_JERSEY,
                    states.IDAHO,
                    states.VIRGINIA,
                    states.GEORGIA,
                    states.MINNESOTA,
                    states.ILLINOIS,
                    states.NEW_MEXICO,
                    states.GETINSURED
                ].some(st => st === stateCode)
                && isConfigNotFound
            );

            return (
                <ApplicantDocumentInfo
                    key={applicantInfo.applicantGuid}
                    {...{
                        applicantInfo: {
                            ...applicantInfo,
                            eventNotSupported,
                        },
                        caseNumber,
                        isUploadHyperDashboardShown,
                        stateCode,
                        onRefresh: refreshDocuments,
                        parentActions: {
                            overrideDocuments,
                            removeDocument,
                            showModal,
                            submitDocuments,
                            updateData,
                            updateEnvironmentProp,
                            updateMockData,
                            uploadDocument,
                            viewUploadedDocument,
                        },
                        userRole,
                    }}
                />
            );
        });
    };

    return (
        <div className="grid-container gi-grid-container">
            <div className="grid-row grid-gap gi-wrapper">
                <aside className = { isUploadHyperDashboardShown ? 'desktop:grid-col-1': 'desktop:grid-col-3' } id="side-nav">
                    <BackToDashboard/>
                </aside>
                <main className="desktop:grid-col-9 qle_document_verification" id="main-content">
                    <div className='header'>
                        <h1>
                            <FormattedMessage
                                id='common.verification_documents.header'
                            />
                        </h1>
                    </div>
                    <FullDataContext.Provider value = { data }>
                        <Fragment>
                            { getContent() }
                        </Fragment>
                    </FullDataContext.Provider>
                </main>
            </div>
        </div>
    );
};

const mapStateToProps = state => ({
    environment: state.common.environment
});

const mapDispatchToProps = dispatch => ({
    actions: bindActionCreators({
        fetchVerificationDocuments,
        overrideDocuments,
        removeDocument,
        showModal,
        submitDocuments,
        updateEnvironmentProp,
        uploadDocument,
        viewUploadedDocument
    }, dispatch)
});

const DocumentsVerificationContainer = connect(mapStateToProps, mapDispatchToProps)(VerificationDocumentsContainer);

const QLEDocumentVerificationContainer = () => {
    return (
        <ContextProvider propName="fileInfo">
            <DocumentsVerificationContainer />
        </ContextProvider>
    );
};

export default QLEDocumentVerificationContainer;